import { ToastService } from './toast.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { signal, WritableSignal } from '@angular/core';
import { environment } from '../../environments/environment';
import { OauthService } from './oauth.service';
import { DbService } from './db.service';
import { paths as pathsMicro } from '../schema/micro';
import { paths as pathsBackend } from '../schema/backend';
import { paths as pathsUserMcp } from '../schema/usermcp';

type CreateResponseMicro = pathsMicro['/create']['post']['responses']['200']['content']['application/json'];
type StatusResponseMicro = pathsMicro['/status']['post']['responses']['200']['content']['application/json'];
type DbCreateResponse = pathsBackend['/db/repo/create']['put']['responses']['200']['content']['application/json'];
type AssignResponseUserMcp = pathsUserMcp['/repo/assign']['get']['responses']['200']['content']['application/json'];

export type Repo = {
  id: string | null;
  name: string;
  type: 'local' | 'cloud';
  created_at: string;
  url: string;
};

export type Chat = {
  id: string;
  label: string;
  date: string;
};

@Injectable({
  providedIn: 'root',
})
export class RepoService {
  constructor(
    private http: HttpClient,
    private oauth: OauthService,
    private toast: ToastService,
    private db: DbService,
  ) {
    // Load repo from sessionStorage on initialization
    const storedRepo = sessionStorage.getItem('repo');
    if (storedRepo) {
      try {
        const parsedRepo = JSON.parse(storedRepo);
        this.repo.set(parsedRepo);
      } catch (error) {
        console.error(
          'Failed to parse stored repo from sessionStorage:',
          error
        );
        sessionStorage.removeItem('repo');
      }
    }
  }

  repo: WritableSignal<Repo | null> = signal<Repo | null>(null);
  private sessionChangedSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);
  sessionChanged$ = this.sessionChangedSubject.asObservable();
  // Add new subject for session label updates
  private sessionLabelUpdatedSubject: BehaviorSubject<{
    sessionId: string;
    label: string;
  } | null> = new BehaviorSubject<{ sessionId: string; label: string } | null>(
    null
  );
  sessionLabelUpdated$ = this.sessionLabelUpdatedSubject.asObservable();

  newSession(sessionId: string | null): void {
    this.sessionChangedSubject.next(sessionId);
  }

  // Add method to notify when a session label is updated
  notifySessionLabelUpdated(sessionId: string, label: string): void {
    this.sessionLabelUpdatedSubject.next({ sessionId, label });
  }

  setRepo(repo: Repo | null): void {
    this.repo.set(repo);
    if (repo) {
      sessionStorage.setItem('repo', JSON.stringify(repo));
    } else {
      sessionStorage.removeItem('repo');
    }
  }

  async getIdFromRepo(serverUrl: string): Promise<string | null> {
    try {
      const response = await lastValueFrom(
        this.http.get<{ repo_id: string | null }>(serverUrl + '/repo/status')
      );
      return response.repo_id;
    } catch (error: unknown) {
      // this.toast.showError('Failed to connect to Study');
      throw new Error('Failed to connect to repo: ' + (error as Error).message, {
        cause: { code: 'REPO_CONNECTION_FAILED', error },
      });
    }
  }

  async fetchRepoDetails(
    repo_id: string,
    serverUrl: string
  ): Promise<Repo | null> {
    let data;
    try {
      data = await this.db.getRepo(repo_id);
    } catch (error) {
      throw new Error('Error fetching repo details:', { cause: error });
    }

    const repoDetails = {
      id: repo_id,
      name: data.name,
      type: data.type as 'local' | 'cloud',
      url: serverUrl,
      created_at: data.created_at,
    };
    this.setRepo(repoDetails);
    return this.repo();
  }

  async startCloudRepo(repoId: string): Promise<void> {
    try {
      const resp = await lastValueFrom(
         this.http.post<CreateResponseMicro>(
          environment.userMcpMicroserviceUrl + '/create',
          {
            username: this.oauth.user?.user_id,
            projectname: repoId
          }
        )
      );
      // console.log('Cloud repo creation initiated for:', repoId);
      let attempts = 0;
      while (attempts <= 4) {
        // console.log(`Checking status of cloud repo (attempt ${attempts + 1})...`);
        const repoStatus = await this.getCloudRepoStatus(repoId);
        if (repoStatus?.returncode === "0") {
          const health = JSON.parse(repoStatus.stdout || '{}');
          // console.log('Cloud repo health status:', health);
          if (health?.Status === 'healthy') break;
        }
        attempts++;
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
      console.warn('Cloud repo status check completed:', resp);
    } catch (error) {
      throw new Error('Failed to start cloud repo: ' + (error as Error).message, {
        cause: { code: 'REPO_START_FAILED', error },
      });
    }
  }

  async getCloudRepoStatus(repoId: string): Promise<StatusResponseMicro> {
    const resp = await lastValueFrom(
      this.http.post<StatusResponseMicro>(environment.userMcpMicroserviceUrl + '/status', {
        username: this.oauth.user?.user_id,
        projectname: repoId
      })
    );
    return resp;
  }

  async createRepo(
    name: string,
    type: 'local' | 'cloud',
    serverUrl: string
  ): Promise<Repo> {
    try {
      const dbResp = await lastValueFrom(
        this.http.put<DbCreateResponse>(
          environment.backendUrl +
          '/db/repo/create' +
          `?name=${encodeURIComponent(name)}&local=${type === 'local'}`,
          {}
        )
      );
      if (type === 'cloud') {
        const repo_id = String(dbResp.id);
        await this.startCloudRepo(repo_id);
      }
      return {
        id: String(dbResp.id),
        name: dbResp.name,
        type: dbResp.type as 'local' | 'cloud',
        url: type === 'cloud' ? environment.userMcpMicroserviceUrl + '/' + this.oauth.user?.user_id + '-' + dbResp.id : serverUrl,
        created_at: new Date().toISOString(),
      };
    } catch (error) {
      throw new Error('Failed to create repo: ' + (error as Error).message, {
        cause: { code: 'REPO_CREATION_FAILED', error },
      });
    }
  }

  async assignRepoId(repo_id: string, serverUrl: string): Promise<void> {
    try {
      await lastValueFrom(
        this.http.get<AssignResponseUserMcp>(serverUrl + '/repo/assign', { params: { repo_id } })
      );
    } catch (error) {
      this.toast.showError('Failed to assign Study ID');
      throw new Error('Failed to assign repo ID: ' + (error as Error).message, {
        cause: error,
      });
    }
  }

  async createAssignRepo(
    name: string,
    type: 'local' | 'cloud',
    serverUrl: string
  ): Promise<void> {
    let createdRepo: Repo;

    // First, create the repository
    try {
      createdRepo = await this.createRepo(name, type, serverUrl);
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (error as any).errorType = 'REPO_CREATION_FAILED';
      throw new Error(`Failed to create repository: ${(error as Error).message}`, {
        cause: error,
      });
    } // Then, assign the repository ID
    try {
      const repoServerUrl = type === 'cloud' ? environment.userMcpMicroserviceUrl + '/' + this.oauth.user?.user_id + '-' + createdRepo.id : serverUrl;
      await this.assignRepoId(createdRepo.id!, repoServerUrl);
      this.setRepo(createdRepo);
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (error as any).errorType = 'REPO_ASSIGNMENT_FAILED';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (error as any).createdRepoId = createdRepo.id;
      throw new Error(
        `Repository was created (ID: ${createdRepo.id}) but assignment failed: ${(error as Error).message}`,
        { cause: error }
      );
    }
  }

  async getRepos(): Promise<Repo[]> {
    let user_id = this.oauth.user?.user_id;
    if (!user_id) {
      const user = await this.oauth.getUser();
      user_id = user?.user_id;
      if (user_id === undefined) {
        throw new Error('Failed to fetch user', {
          cause: { code: 'FETCH_USER_ERROR' },
        });
      }
    }
    const data = await this.db.listRepos();
    return data.map((repo) => ({
      id: repo.id,
      name: repo.name,
      type: repo.type as 'local' | 'cloud',
      created_at: repo.created_at,
      url: '',
    }));
  }

  async getChats(): Promise<Chat[]> {
    if (!this.oauth.user) {
      throw new Error('User info is not available', {
        cause: { code: 'USER_INFO_NOT_AVAILABLE' },
      });
    }
    const repoId = this.repo()?.id;
    if (!repoId) {
      return [];
    }
    let data;
    try {
      data = await this.db.listChats(repoId);
    } catch (error) {
      throw new Error('Failed to fetch chats', {
        cause: { code: 'FETCH_CHATS_ERROR', error },
      });
    }
    const chats = data
      .map((chat) => ({
        id: chat.session_id,
        label: chat.label || `Chat ${chat.id}`,
        date: chat.created_at,
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()); // Sort by date descending (most recent first)
    return chats;
  }

  // async test(): Promise<void> {
  //   const resp = await lastValueFrom(this.http.get('https://usermcpserver.ailens.ai/6b4c5a7c-703a-4ee7-b093-65c03b9ea220-62/repo/assign?repo_id=5'));
  //   console.log('Test response:', resp);
  //   if (resp.returncode !== 0) {
  //     this.toast.showError('Failed to test cloud repository');
  //     throw new Error('Failed to test cloud repo: ' + resp.message, {
  //       cause: { code: 'repo_test_failed', response: resp },
  //     });
  //   }
  // }
}
